<template>
	<div class="branch-rule">
		<div class="branch-header display-flex">
			<div class="branch-name-container flex-1">
				<div class="branch-name-edit display-flex">
					<input
						type="text"
						:readonly="!hasEditPermissions"
						v-model="branchName"
						ref="branchName"
						class="branch-name-input"
						:size="branchName.length + 4"
					/>
					<v-btn v-if="hasEditPermissions" icon small @click="$refs.branchName.focus()">
						<div class="edit-icon"><EditIcon /></div>
					</v-btn>
				</div>
			</div>

			<template v-if="enableDelete">
				<AppLoader v-if="isLoadingDelete" class="mt-1" />
				<div v-else class="delete-button">
					<v-btn icon small @click="deleteRule"><v-icon>mdi-delete</v-icon></v-btn>
				</div>
			</template>
		</div>

		<div class="branch-rule-body">
			<div v-if="isDefault" class="default-rule flex-centerize">If no branch condition is met, run this branch</div>
			<div class="rule-container" v-else>
				<div class="instruction">Run this branch if...</div>
				<div no-gutters class="conditions-container flex-space-between--center" v-if="rulesParams.length">
					<div class="condition flex-1" v-for="ruleParam in rulesParams" :key="ruleParam.key">
						<TableFilterConditionGenerator
							@onChange="(value) => updateValueInRule({ key: ruleParam.key, value })"
							:param="ruleParam"
							:node="editNode"
							:rules="rules(ruleParam)"
							:readonly="!hasEditPermissions"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import debounce from 'lodash/debounce';
import TableFilterService from '@/components/BlockEditor/TableActionEditor/TableFilterConditionGenerator/table-filter-service';
import TableFilterConditionGenerator from '@/components/BlockEditor/TableActionEditor/TableFilterConditionGenerator/TableFilterConditionGenerator';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
import { mapGetters } from 'vuex';
import EditIcon from './assets/edit.svg';

export default {
	name: 'BranchRule',
	props: {
		item: { type: Object, required: true },
		enableDelete: { type: Boolean },
		hasEditPermissions: { type: Boolean },
		isLoadingDelete: { type: Boolean },
	},
	components: {
		TableFilterConditionGenerator,
		EditIcon,
	},
	computed: {
		...mapGetters('selectedNode', ['editNode']),
		ruleId() {
			return this.item.id;
		},
		rulesParams() {
			return [{ key: 'rules', name: 'Filter by rules', type: 'rules', required: true, description: 'Rules to filter each branch by' }];
		},
		isDefault() {
			return this.item.else;
		},
	},
	watch: {
		branchName(newV) {
			this.updateValueInRule({ key: 'name', value: newV });
		},
	},
	methods: {
		updateValueInRule: debounce(function (payload) {
			const { key, value } = payload;
			this.$emit('updateRule', { id: this.ruleId, key, value });
		}, 500),
		deleteRule() {
			this.$emit('deleteRule', { id: this.ruleId });
		},
		rules(ruleParam) {
			const allowedKeysBackwardCompatibility = ['field', 'condition', 'condition_value'];
			if (!this.item[ruleParam.key] && allowedKeysBackwardCompatibility.some((field) => !isNullOrUndefinedOrEmpty(this.item[field]))) {
				return TableFilterService.getInitialTableFilterObjWithValue({
					source: this.item.field,
					condition: this.item.condition,
					conditionValue: this.item.condition_value,
				});
			}
			return this.item[ruleParam.key];
		},
	},
	data() {
		return {
			branchName: this.item.name,
		};
	},
};
</script>

<style scoped lang="scss">
.branch-rule {
	margin: 0 0 10px;
	background-color: #fcfcfd;
	border: 1px solid #d1d9e9;
	border-radius: 9px;
	font-size: 12px;

	.branch-header {
		padding: 10px 15px;
		background-color: #f1f5f8;
		font-size: 14px;
		border-radius: 9px 9px 0 0;

		.delete-button,
		.edit-icon {
			opacity: 0;
			transition: 0.5s;
			transform: scale(0.5);
		}

		&:hover {
			.delete-button,
			.edit-icon {
				opacity: 1;
				transition: 0.2s;
				transform: scale(0.8);
			}
		}

		input.branch-name-input {
			outline: none;
		}
	}

	.branch-rule-body {
		padding: 10px 15px;
	}
	.rule-container {
		.instruction {
			font-size: 14px;
			color: #707c8a;
		}

		.conditions-container {
			.condition {
				&:not(:first-child) {
					margin: 0 0 0 10px;
				}
				&:last-child ::v-deep .insert-data-container {
					right: 0;
				}
			}
		}
	}

	.default-rule {
		height: 50px;
	}
}
</style>
